import React, { useState, useEffect } from 'react';
import Sidebar from './components/shared/Sidebar';
import Songs from './pages/Songs/Songs';
import SongDetails from './pages/SongDetails/SongDetails';
import Artists from './pages/Artists/Artists';
import Distributor from './pages/Distributor/Distributor';
import Ringtone from './pages/Ringtone/Ringtone';
import Albums from './pages/Albums/Albums';
import AlbumDetails from './pages/AlbumDetails/AlbumDetails';
import Ownership from './pages/Ownership/Ownership';
import NotesCases from './pages/NotesCases/NotesCases';
import RecodeLabels from './pages/RecodeLabels/RecodeLabels';
import ArtistDetails from './pages/ArtistDetails/ArtistDetails';
import EAccounts from './pages/EAccounts/EAccounts';
import Calendar from './pages/Calendar/Calendar';
import Revenue from './pages/Revenue/Revenue';
import Settings from './pages/Settings/Settings';
import Profile from './pages/Profile/Profile';
import Login from './pages/Auth/Login';
import ResetPassword from './pages/Auth/ResetPassword';
import NotFound from './components/shared/NotFound';
import LoadingOverlay from './components/shared/LoadingOverlay';
import { AuthProvider, useAuth } from './context/AuthContext';
import { apiClient } from './api/apiClient';
import { toast } from './components/shared/toast';
import './components/shared/Toast.css';
import './App.css';

function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    return toast.subscribe((newToast) => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { ...newToast, id }]);
      
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, 4000);
    });
  }, []);

  const handleDismiss = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <div className="toast-container-wrapper">
      {toasts.map((t) => (
        <div key={t.id} className={`toast-item ${t.type}`}>
          <span className="toast-icon">
            {t.type === 'success' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : t.type === 'warning' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            ) : t.type === 'info' ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            )}
          </span>
          <span className="toast-message">{t.message}</span>
          <button 
            type="button" 
            className="toast-close-btn" 
            onClick={() => handleDismiss(t.id)}
            aria-label="Close notification"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const ROUTE_MAP = {
  '/': 'Songs',
  '/songs': 'Songs',
  '/songs/details': 'SongDetails',
  '/artists': 'Artists',
  '/artists/details': 'ArtistDetails',
  '/distributor': 'Distributor',
  '/distributor/details': 'DistributorDetails',
  '/ownership': 'Ownership',
  '/notes-cases': 'Notes & Cases',
  '/notes-and-cases': 'Notes & Cases',
  '/recode-labels': 'Recode Labels',
  '/recode-labels/details': 'RecodeLabelsDetails',
  '/ringtone': 'Ringtone',
  '/albums': 'Albums',
  '/albums/details': 'AlbumDetails',
  '/e-accounts': 'E-Accounts',
  '/e-accounts/emails': 'E-Accounts',
  '/e-accounts/domains': 'E-Accounts',
  '/e-accounts/servers': 'E-Accounts',
  '/e-accounts/social-account': 'E-Accounts',
  '/e-accounts/subscriptions': 'E-Accounts',
  '/calendar': 'Calendar',
  '/callander': 'Calendar',
  '/revenue': 'Revenue',
  '/settings': 'Settings',
  '/settings/users': 'Settings',
  '/settings/roles': 'Settings',
  '/settings/logs': 'Settings',
  '/profile': 'Profile',
  '/login': 'Login',
  '/reset-password': 'ResetPassword',
};

const PAGE_TO_PATH_MAP = {
  'Songs': '/songs',
  'Artists': '/artists',
  'Distributor': '/distributor',
  'Ownership': '/ownership',
  'Notes & Cases': '/notes-cases',
  'Recode Labels': '/recode-labels',
  'Ringtone': '/ringtone',
  'Albums': '/albums',
  'E-Accounts': '/e-accounts/emails',
  'Calendar': '/calendar',
  'Callander': '/calendar',
  'Revenue': '/revenue',
  'Settings': '/settings/users',
  'Profile': '/profile',
};

function AppContent() {
  const { isAuthenticated, isLoading } = useAuth();
  const [currentPath, setCurrentPath] = useState(window.location.pathname + window.location.search);
  const [selectedSong, setSelectedSong] = useState(window.history.state?.selectedSong || null);
  const [selectedAlbum, setSelectedAlbum] = useState(window.history.state?.selectedAlbum || null);
  const [selectedArtist, setSelectedArtist] = useState(window.history.state?.selectedArtist || null);
  const [selectedCase, setSelectedCase] = useState(window.history.state?.selectedCase || null);
  const [selectedDistributor, setSelectedDistributor] = useState(window.history.state?.selectedDistributor || null);
  const [selectedLabel, setSelectedLabel] = useState(window.history.state?.selectedLabel || null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Sync state with browser Back / Forward arrows (popstate)
  useEffect(() => {
    const handlePopState = (event) => {
      const fullPath = window.location.pathname + window.location.search;
      setCurrentPath(fullPath);

      const hState = event?.state || window.history.state || {};
      const path = window.location.pathname;

      if (path === '/songs/details') {
        if (hState.selectedSong) setSelectedSong(hState.selectedSong);
      } else {
        setSelectedSong(null);
      }

      if (path === '/artists/details') {
        if (hState.selectedArtist) setSelectedArtist(hState.selectedArtist);
      } else {
        setSelectedArtist(null);
      }

      if (path === '/albums/details') {
        if (hState.selectedAlbum) setSelectedAlbum(hState.selectedAlbum);
      } else {
        setSelectedAlbum(null);
      }

      if (path === '/distributor/details') {
        if (hState.selectedDistributor) setSelectedDistributor(hState.selectedDistributor);
      } else {
        setSelectedDistributor(null);
      }

      if (path === '/recode-labels/details') {
        if (hState.selectedLabel) setSelectedLabel(hState.selectedLabel);
      } else {
        setSelectedLabel(null);
      }

      if (path.startsWith('/notes-cases') || path.startsWith('/notes-and-cases')) {
        if (hState.selectedCase) setSelectedCase(hState.selectedCase);
      } else {
        setSelectedCase(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Automatic rehydration if user visits or refreshes a detail URL directly
  useEffect(() => {
    const path = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const id = searchParams.get('id');

    if (!id) return;

    if (path === '/songs/details' && !selectedSong) {
      apiClient(`/songs/${id}`).then(res => {
        if (res && res.id) setSelectedSong(res);
      }).catch(err => console.warn('Failed to rehydrate song:', err));
    } else if (path === '/artists/details' && !selectedArtist) {
      apiClient(`/artists?search=${id}`).then(res => {
        const found = res?.artists?.find(a => String(a.id) === String(id) || a.code === id);
        if (found) setSelectedArtist(found);
      }).catch(err => console.warn('Failed to rehydrate artist:', err));
    } else if (path === '/albums/details' && !selectedAlbum) {
      apiClient(`/albums/${id}`).then(res => {
        if (res && res.id) setSelectedAlbum(res);
      }).catch(err => console.warn('Failed to rehydrate album:', err));
    } else if (path === '/distributor/details' && !selectedDistributor) {
      apiClient(`/distributors/${id}`).then(res => {
        if (res && res.id) setSelectedDistributor(res);
      }).catch(err => console.warn('Failed to rehydrate distributor:', err));
    } else if (path === '/recode-labels/details' && !selectedLabel) {
      apiClient(`/record-label/${id}/details`).then(res => {
        if (res && res.id) setSelectedLabel(res);
      }).catch(err => console.warn('Failed to rehydrate label:', err));
    }
  }, [currentPath]);

  useEffect(() => {
    if (window.location.pathname === '/e-accounts') {
      window.history.replaceState({}, '', '/e-accounts/emails');
      setCurrentPath('/e-accounts/emails');
    }
    if (window.location.pathname === '/settings') {
      window.history.replaceState({}, '', '/settings/users');
      setCurrentPath('/settings/users');
    }
  }, [currentPath]);

  useEffect(() => {
    if (!isLoading) {
      if (!isAuthenticated) {
        if (!currentPath.startsWith('/reset-password') && currentPath !== '/login' && currentPath !== '/') {
          window.history.replaceState({}, '', '/login');
          setCurrentPath('/login');
        }
      } else {
        if (currentPath === '/' || currentPath === '/login') {
          window.history.replaceState({}, '', '/songs');
          setCurrentPath('/songs');
        }
      }
    }
  }, [isAuthenticated, isLoading, currentPath]);

  const navigate = (path, state = {}) => {
    window.history.pushState(state, '', path);
    setCurrentPath(path);
  };

  const handleSongSelect = (song) => {
    setSelectedSong(song);
    const target = song?.id ? `/songs/details?id=${song.id}` : '/songs/details';
    navigate(target, { selectedSong: song });
  };

  const handleBackToSongs = () => {
    setSelectedSong(null);
    navigate('/songs');
  };

  const handleArtistSelect = (artist) => {
    setSelectedArtist(artist);
    const target = artist?.id ? `/artists/details?id=${artist.id}` : '/artists/details';
    navigate(target, { selectedArtist: artist });
  };

  const handleBackToArtists = () => {
    setSelectedArtist(null);
    navigate('/artists');
  };

  const handleAlbumSelect = (album) => {
    setSelectedAlbum(album);
    const target = album?.id ? `/albums/details?id=${album.id}` : '/albums/details';
    navigate(target, { selectedAlbum: album });
  };

  const handleBackToAlbums = () => {
    setSelectedAlbum(null);
    navigate('/albums');
  };

  const handleDistributorSelect = (distributor) => {
    setSelectedDistributor(distributor);
    const target = distributor?.id ? `/distributor/details?id=${distributor.id}` : '/distributor/details';
    navigate(target, { selectedDistributor: distributor });
  };

  const handleBackToDistributors = () => {
    setSelectedDistributor(null);
    navigate('/distributor');
  };

  const handleLabelSelect = (label) => {
    setSelectedLabel(label);
    const target = label?.id ? `/recode-labels/details?id=${label.id}` : '/recode-labels/details';
    navigate(target, { selectedLabel: label });
  };

  const handleBackToLabels = () => {
    setSelectedLabel(null);
    navigate('/recode-labels');
  };

  const handleCaseSelect = (caseItem) => {
    setSelectedCase(caseItem);
    const targetPath = caseItem?.id ? `/notes-cases?id=${caseItem.id}` : '/notes-cases';
    navigate(targetPath, { selectedCase: caseItem });
  };

  const getPageAndTab = (pathStr) => {
    const pathWithoutSearch = pathStr.split('?')[0];
    const normalized = pathWithoutSearch.replace(/\/$/, '') || '/';
    
    if (normalized.startsWith('/e-accounts')) {
      const parts = normalized.split('/');
      const subTab = parts[2] || 'emails';
      const validTabs = ['emails', 'domains', 'servers', 'social-account', 'subscriptions'];
      if (validTabs.includes(subTab)) {
        return { page: 'E-Accounts', subTab };
      } else {
        return { page: '404', subTab: null };
      }
    }

    if (normalized.startsWith('/settings')) {
      const parts = normalized.split('/');
      const subTab = parts[2] || 'users';
      const validTabs = ['users', 'roles', 'logs'];
      if (validTabs.includes(subTab)) {
        return { page: 'Settings', subTab };
      } else {
        return { page: '404', subTab: null };
      }
    }
    
    if (normalized.startsWith('/notes-cases') || normalized.startsWith('/notes-and-cases')) {
      return { page: 'Notes & Cases', subTab: null };
    }

    if (normalized === '/songs/details') {
      return { page: 'SongDetails', subTab: null };
    }
    if (normalized === '/artists/details') {
      return { page: 'ArtistDetails', subTab: null };
    }
    if (normalized === '/albums/details') {
      return { page: 'AlbumDetails', subTab: null };
    }
    if (normalized === '/distributor/details') {
      return { page: 'DistributorDetails', subTab: null };
    }
    if (normalized === '/recode-labels/details') {
      return { page: 'RecodeLabelsDetails', subTab: null };
    }

    const page = ROUTE_MAP[normalized];
    if (page) {
      return { page, subTab: null };
    }
    
    return { page: '404', subTab: null };
  };

  const { page: activePage, subTab } = getPageAndTab(currentPath);

  // 1. Loading State
  if (isLoading) {
    return <LoadingOverlay isLoading message="Authenticating session..." />;
  }

  // 2. Unauthenticated Route Guard
  if (!isAuthenticated) {
    if (currentPath.startsWith('/reset-password')) {
      return (
        <>
          <ResetPassword onRedirectLogin={() => navigate('/login')} />
          <ToastContainer />
        </>
      );
    }
    return (
      <>
        <Login onLoginSuccess={() => navigate('/songs')} />
        <ToastContainer />
      </>
    );
  }

  // 3. Authenticated App Layout
  const renderContent = () => {
    switch (activePage) {
      case 'Profile':
        return <Profile />;

      case 'Songs':
        return <Songs onSelectSong={handleSongSelect} />;

      case 'SongDetails':
        return selectedSong ? (
          <SongDetails 
            song={selectedSong} 
            onBack={handleBackToSongs} 
            onSelectCase={handleCaseSelect}
            onSelectAlbum={handleAlbumSelect}
          />
        ) : (
          <Songs onSelectSong={handleSongSelect} />
        );

      case 'Artists':
        return <Artists onSelectArtist={handleArtistSelect} />;

      case 'ArtistDetails':
        return selectedArtist ? (
          <ArtistDetails 
            artist={selectedArtist} 
            onBack={handleBackToArtists} 
            onSelectSong={handleSongSelect}
            onSelectCase={handleCaseSelect}
            onSelectAlbum={handleAlbumSelect}
          />
        ) : (
          <Artists onSelectArtist={handleArtistSelect} />
        );

      case 'Albums':
        return <Albums onSelectAlbum={handleAlbumSelect} onSelectSong={handleSongSelect} />;

      case 'AlbumDetails':
        return selectedAlbum ? (
          <AlbumDetails 
            album={selectedAlbum} 
            onBack={handleBackToAlbums} 
            onSelectSong={handleSongSelect}
            onSelectCase={handleCaseSelect}
            onEdit={(updated) => setSelectedAlbum(updated)}
            onDelete={handleBackToAlbums}
          />
        ) : (
          <Albums onSelectAlbum={handleAlbumSelect} onSelectSong={handleSongSelect} />
        );

      case 'Distributor':
        return (
          <Distributor 
            onSelectSong={handleSongSelect} 
            onSelectCase={handleCaseSelect} 
            selectedDistributor={selectedDistributor}
            onSelectDistributor={handleDistributorSelect}
            onBackFromDistributor={handleBackToDistributors}
          />
        );

      case 'DistributorDetails':
        return (
          <Distributor 
            onSelectSong={handleSongSelect} 
            onSelectCase={handleCaseSelect} 
            selectedDistributor={selectedDistributor}
            onSelectDistributor={handleDistributorSelect}
            onBackFromDistributor={handleBackToDistributors}
          />
        );

      case 'Recode Labels':
        return (
          <RecodeLabels 
            onSelectSong={handleSongSelect} 
            selectedLabel={selectedLabel}
            onSelectLabel={handleLabelSelect}
            onBackFromLabel={handleBackToLabels}
          />
        );

      case 'RecodeLabelsDetails':
        return (
          <RecodeLabels 
            onSelectSong={handleSongSelect} 
            selectedLabel={selectedLabel}
            onSelectLabel={handleLabelSelect}
            onBackFromLabel={handleBackToLabels}
          />
        );

      case 'Ownership':
        return <Ownership onSelectSong={handleSongSelect} />;

      case 'Notes & Cases':
        return <NotesCases initialCase={selectedCase} onClearInitialCase={() => setSelectedCase(null)} />;

      case 'E-Accounts':
        return <EAccounts activeSubTab={subTab} onNavigate={navigate} />;

      case 'Calendar':
        return <Calendar />;

      case 'Revenue':
        return <Revenue onSelectSong={handleSongSelect} />;

      case 'Settings':
        return <Settings activeSubTab={subTab} onNavigate={navigate} />;

      case 'Ringtone':
        return <Ringtone onSelectSong={handleSongSelect} />;

      case '404':
      default:
        return <NotFound onNavigateHome={() => navigate('/songs')} />;
    }
  };

  return (
    <div className={`app-shell ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <Sidebar
        activePage={
          activePage === 'SongDetails' ? 'Songs' :
          activePage === 'ArtistDetails' ? 'Artists' :
          activePage === 'AlbumDetails' ? 'Albums' :
          activePage === 'DistributorDetails' ? 'Distributor' :
          activePage === 'RecodeLabelsDetails' ? 'Recode Labels' :
          activePage
        }
        onSelect={(page) => {
          const targetPath = PAGE_TO_PATH_MAP[page];
          setSelectedSong(null);
          setSelectedAlbum(null);
          setSelectedArtist(null);
          setSelectedCase(null);
          setSelectedDistributor(null);
          setSelectedLabel(null);
          if (targetPath) {
            navigate(targetPath);
          } else {
            navigate('/songs');
          }
        }}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      
      {/* Main Content Area */}
      <main className="main-content">
        {renderContent()}
      </main>

      {/* Toast Overlay */}
      <ToastContainer />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
